import { useEffect, useMemo, useRef, useState } from 'react'
import { Pressable } from '../../Common/Pressable'
import { columnCompletionPercent, type SandColumn as SandColumnModel } from './useSandfallModel'

type Grain = {
  id: string
  x: number
  delay: number
  size: number
}

const VIEWBOX_WIDTH = 120
const VIEWBOX_HEIGHT = 300
const SAND_TOP = 34
const SAND_BOTTOM = 260
const SAND_HEIGHT = SAND_BOTTOM - SAND_TOP

const formatWeight = (value: number): string =>
  value >= 10 ? value.toFixed(0) : value.toFixed(value % 1 === 0 ? 0 : 1)

export const SandfallColumn = ({
  column,
  isSelected,
  reducedMotion,
  animateDrain = true,
  onSelect,
}: {
  column: SandColumnModel
  isSelected?: boolean
  reducedMotion: boolean
  animateDrain?: boolean
  onSelect?: () => void
}) => {
  const previousRemaining = useRef(column.remainingWeight)
  const [grains, setGrains] = useState<Grain[]>([])
  const safeId = column.goalId.replace(/[^a-zA-Z0-9_-]/g, '-')
  const fillRatio = column.totalWeight > 0 ? Math.max(0, Math.min(1, column.remainingWeight / column.totalWeight)) : 0
  const filledHeight = SAND_HEIGHT * fillRatio
  const completion = columnCompletionPercent(column)

  useEffect(() => {
    const previous = previousRemaining.current
    previousRemaining.current = column.remainingWeight
    if (reducedMotion || !animateDrain || column.remainingWeight >= previous) return

    let frame = 0
    frame = window.requestAnimationFrame(() => {
      const count = Math.max(6, Math.min(12, Math.round((previous - column.remainingWeight) * 1.5)))
      const burst = Array.from({ length: count }, (_, index) => ({
        id: `${column.goalId}-${Date.now()}-${index}`,
        x: 38 + ((index * 17) % 46),
        delay: index * 28,
        size: 1.8 + (index % 3) * 0.6,
      }))
      setGrains(burst)
      window.setTimeout(() => setGrains((current) => current.filter((grain) => !burst.some((item) => item.id === grain.id))), 1050)
    })
    return () => window.cancelAnimationFrame(frame)
  }, [animateDrain, column.goalId, column.remainingWeight, reducedMotion])

  const bandRects = useMemo(() => {
    const remainingTotal = column.bands.reduce((sum, band) => sum + Math.max(0, band.remaining), 0)
    let cursor = SAND_BOTTOM
    return column.bands.map((band) => {
      const height = remainingTotal > 0 ? (Math.max(0, band.remaining) / remainingTotal) * filledHeight : 0
      cursor -= height
      return {
        ...band,
        y: cursor,
        height,
      }
    })
  }, [column.bands, filledHeight])

  return (
    <Pressable
      className={`sandfallColumn${isSelected ? ' isSelected' : ''}`}
      onClick={onSelect}
      aria-label={`${column.label}: ${formatWeight(column.remainingWeight)} of ${formatWeight(column.totalWeight)} weight remaining`}
    >
      <svg className="sandfallColumn__svg" viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`} role="img" aria-hidden="true">
        <defs>
          <clipPath id={`sandfall-clip-${safeId}`}>
            <path d="M34 35 C34 24 86 24 86 35 L78 260 C78 272 42 272 42 260 Z" />
          </clipPath>
          <linearGradient id={`sandfall-glass-${safeId}`} x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" style={{ stopColor: 'color-mix(in srgb, var(--text-strong) 30%, transparent)' }} />
            <stop offset="48%" style={{ stopColor: 'color-mix(in srgb, var(--text-strong) 4%, transparent)' }} />
            <stop offset="100%" style={{ stopColor: 'color-mix(in srgb, var(--text-strong) 16%, transparent)' }} />
          </linearGradient>
        </defs>
        <path className="sandfallColumn__glass" d="M34 35 C34 24 86 24 86 35 L78 260 C78 272 42 272 42 260 Z" />
        <g clipPath={`url(#sandfall-clip-${safeId})`}>
          <rect x="28" y={SAND_TOP} width="64" height={SAND_HEIGHT} className="sandfallColumn__well" />
          {bandRects.map((band) => (
            <rect
              key={band.taskId}
              className="sandfallColumn__sandBand"
              x="28"
              y={band.y}
              width="64"
              height={band.height}
              fill={band.colorToken}
            />
          ))}
          <rect x="28" y="34" width="64" height={SAND_HEIGHT} fill={`url(#sandfall-glass-${safeId})`} />
        </g>
        <path className="sandfallColumn__rim" d="M34 35 C34 24 86 24 86 35 M42 260 C42 272 78 272 78 260" />
        {grains.map((grain) => (
          <circle
            key={grain.id}
            className="sandfallColumn__grain"
            style={{ animationDelay: `${grain.delay}ms` }}
            cx={grain.x}
            cy={SAND_BOTTOM - Math.max(12, filledHeight)}
            r={grain.size}
            fill={column.colorToken}
          />
        ))}
      </svg>
      <span className="sandfallColumn__meta">
        <strong>{column.label}</strong>
        <span>{formatWeight(column.remainingWeight)} left - {Math.round(completion)}% drained</span>
      </span>
    </Pressable>
  )
}
